import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { PALETA, formatar, formatarCurto, diaCurto } from "./formatos";

// Gráficos do BI em Recharts, tema escuro do brand system.

const GRID = "rgba(255,255,255,0.06)";
const TICK = { fill: "#71717A", fontSize: 11 };

function TooltipBox({ active, payload, label, metrics, labelFmt }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-surface-3 border border-white/[0.1] rounded-lg px-3 py-2 text-xs shadow-xl">
            <p className="text-ink-3 mb-1">{labelFmt ? labelFmt(label) : label}</p>
            {payload.map(p => {
                const m = metrics.find(x => x.key === (p.dataKey || "").replace(/__ant$/, ""));
                return <p key={p.dataKey} style={{ color: p.color }}>{p.name}: <strong>{formatar(p.value, m?.formato)}</strong></p>;
            })}
        </div>
    );
}

const rotulo = (r, dim) => dim === "dia" ? diaCurto(r.dia) : (r[`${dim}_label`] || r[dim] || "(sem valor)");

export function LineWidget({ data, metrics, dim }) {
    const rows = (data.rows || []).map(r => ({ ...r, _x: rotulo(r, dim) }));
    // comparativo: alinha o período anterior por índice (dia N ↔ dia N)
    const comp = data.comparativo?.rows || null;
    if (comp) rows.forEach((r, i) => { metrics.forEach(m => { r[`${m.key}__ant`] = comp[i]?.[m.key] ?? null; }); });
    const fmt0 = metrics[0]?.formato;
    const multiEixo = metrics.length > 1 && metrics[0].formato !== metrics[1].formato;
    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="_x" tick={TICK} axisLine={false} tickLine={false} minTickGap={16} />
                <YAxis yAxisId="l" tick={TICK} axisLine={false} tickLine={false} width={54} tickFormatter={v => formatarCurto(v, fmt0)} />
                {multiEixo && <YAxis yAxisId="r" orientation="right" tick={TICK} axisLine={false} tickLine={false} width={54} tickFormatter={v => formatarCurto(v, metrics[1].formato)} />}
                <Tooltip content={<TooltipBox metrics={metrics} />} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />
                {metrics.map((m, i) => (
                    <Line key={m.key} yAxisId={multiEixo && i > 0 ? "r" : "l"} type="monotone" dataKey={m.key} name={m.label} stroke={PALETA[i % PALETA.length]} strokeWidth={2} dot={rows.length <= 31} activeDot={{ r: 4 }} connectNulls />
                ))}
                {comp && metrics.map((m, i) => (
                    <Line key={m.key + "__ant"} yAxisId={multiEixo && i > 0 ? "r" : "l"} type="monotone" dataKey={`${m.key}__ant`} name={`${m.label} (anterior)`} stroke={PALETA[i % PALETA.length]} strokeOpacity={0.45} strokeDasharray="4 4" strokeWidth={1.5} dot={false} connectNulls />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

export function BarWidget({ data, metrics, dim, horizontal, empilhado }) {
    const rows = (data.rows || []).map(r => ({ ...r, _x: rotulo(r, dim) }));
    const fmt0 = metrics[0]?.formato;
    if (horizontal) {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 0 }} barCategoryGap={6}>
                    <CartesianGrid stroke={GRID} horizontal={false} />
                    <XAxis type="number" tick={TICK} axisLine={false} tickLine={false} tickFormatter={v => formatarCurto(v, fmt0)} />
                    <YAxis type="category" dataKey="_x" tick={{ ...TICK, fill: "#D4D4D8" }} axisLine={false} tickLine={false} width={170} tickFormatter={v => String(v).length > 26 ? String(v).slice(0, 25) + "…" : v} />
                    <Tooltip content={<TooltipBox metrics={metrics} />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                    {metrics.length > 1 && <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />}
                    {metrics.map((m, i) => <Bar key={m.key} dataKey={m.key} name={m.label} fill={PALETA[i % PALETA.length]} radius={[0, 4, 4, 0]} stackId={empilhado ? "s" : undefined} />)}
                </BarChart>
            </ResponsiveContainer>
        );
    }
    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barCategoryGap={rows.length > 20 ? 1 : 6}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="_x" tick={TICK} axisLine={false} tickLine={false} minTickGap={12} tickFormatter={v => String(v).length > 14 ? String(v).slice(0, 13) + "…" : v} />
                <YAxis tick={TICK} axisLine={false} tickLine={false} width={54} tickFormatter={v => formatarCurto(v, fmt0)} />
                <Tooltip content={<TooltipBox metrics={metrics} />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                {metrics.length > 1 && <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />}
                {metrics.map((m, i) => <Bar key={m.key} dataKey={m.key} name={m.label} fill={PALETA[i % PALETA.length]} radius={[4, 4, 0, 0]} stackId={empilhado ? "s" : undefined} />)}
            </BarChart>
        </ResponsiveContainer>
    );
}

export function DonutWidget({ data, metric, dim }) {
    const rows = (data.rows || []).map(r => ({ name: rotulo(r, dim), value: Number(r[metric.key] || 0) })).filter(r => r.value > 0);
    const total = rows.reduce((s, r) => s + r.value, 0);
    return (
        <div className="h-full grid grid-cols-[1fr_minmax(120px,45%)] gap-2 items-center">
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie data={rows} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="88%" paddingAngle={2} stroke="none">
                        {rows.map((_, i) => <Cell key={i} fill={PALETA[i % PALETA.length]} />)}
                    </Pie>
                    <Tooltip content={({ active, payload }) => active && payload?.length ? (
                        <div className="bg-surface-3 border border-white/[0.1] rounded-lg px-3 py-2 text-xs shadow-xl">
                            <p className="text-ink-2">{payload[0].name}</p>
                            <p className="font-semibold">{formatar(payload[0].value, metric.formato)} · {total ? ((payload[0].value / total) * 100).toFixed(1) : 0}%</p>
                        </div>) : null} />
                </PieChart>
            </ResponsiveContainer>
            <ul className="text-[11px] space-y-1 overflow-y-auto max-h-full pr-1">
                {rows.map((r, i) => (
                    <li key={i} className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PALETA[i % PALETA.length] }} />
                        <span className="truncate text-ink-3 flex-1" title={r.name}>{r.name}</span>
                        <span className="tabular-nums text-ink-1">{total ? ((r.value / total) * 100).toFixed(0) : 0}%</span>
                    </li>
                ))}
                {rows.length === 0 && <li className="text-ink-5">Sem dados</li>}
            </ul>
        </div>
    );
}
